import styled from 'styled-components';
import { designTokens } from '../foundation/DesignTokens';

// Main table container
export const TableContainer = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.95rem;

  @media (max-width: 900px) {
    font-size: 0.85rem;
  }
`;

// Table header
export const TableHead = styled.thead`
  background: ${designTokens.colors.gray[50]};
  border-top-left-radius: ${designTokens.radius.md};
  border-top-right-radius: ${designTokens.radius.md};
`;

// Table header cell
export const TableHeaderCell = styled.th`
  text-align: left;
  padding: 14px 24px;
  background: ${designTokens.colors.gray[50]};
  color: ${designTokens.colors.gray[500]};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${designTokens.colors.gray[200]};
  white-space: nowrap;

  &:first-child {
    border-top-left-radius: 0;
  }

  &:last-child {
    border-top-right-radius: 0;
  }

  @media (max-width: 900px) {
    padding: 12px 16px;
    font-size: 0.7rem;
  }
`;

// Table body
export const TableBody = styled.tbody``;

// Table row
export const TableRow = styled.tr`
  transition: background-color 0.15s ease;

  &:hover {
    background: ${designTokens.colors.gray[50]};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

// Table cell
export const TableCell = styled.td`
  padding: 16px 24px;
  border-bottom: 1px solid ${designTokens.colors.gray[100]};
  color: ${designTokens.colors.gray[800]};
  vertical-align: middle;

  @media (max-width: 900px) {
    padding: 12px 16px;
  }
`;

// Toolbar for table actions and search
export const TableToolbar = styled.div`
  padding: 16px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${designTokens.spacing.lg};
  flex-wrap: wrap;

  @media (max-width: 900px) {
    padding: 12px 16px 8px;
    flex-direction: column;
    align-items: flex-start;
    gap: ${designTokens.spacing.md};
  }
`;

// Left side of toolbar
export const TableToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  flex-wrap: wrap;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

// Right side of toolbar
export const TableToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  flex-wrap: wrap;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

// Label for bulk actions
export const TableBulkLabel = styled.span`
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[500]};
  font-weight: 500;

  @media (max-width: 900px) {
    font-size: 0.75rem;
  }
`;

// Table footer with pagination
export const TableFooter = styled.div`
  padding: 10px 24px 16px;
  border-top: 1px solid ${designTokens.colors.gray[100]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${designTokens.spacing.md};

  @media (max-width: 900px) {
    padding: 10px 16px 14px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

// Summary text showing item counts
export const TableSummary = styled.div`
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[500]};

  @media (max-width: 900px) {
    font-size: 0.75rem;
  }
`;

// Pagination container
export const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: center;
  }
`;

// Pagination button (prev/next)
export const PageButton = styled.button`
  min-width: 30px;
  padding: 6px 10px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid ${designTokens.colors.gray[200]};
  background: white;
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[700]};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${designTokens.colors.gray[50]};
    border-color: ${designTokens.colors.gray[300]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${designTokens.colors.gray[50]};
  }

  @media (max-width: 900px) {
    min-width: 28px;
    padding: 5px 8px;
    font-size: 0.75rem;
  }
`;

// Page number button
export const PageNumber = styled.button`
  min-width: 30px;
  padding: 6px 10px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid ${designTokens.colors.gray[200]};
  background: white;
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[700]};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${designTokens.colors.gray[50]};
    border-color: ${designTokens.colors.gray[300]};
  }

  ${(props) =>
    props.active &&
    `
    background: ${designTokens.colors.primary[500]};
    border-color: ${designTokens.colors.primary[500]};
    color: white;
    font-weight: 600;

    &:hover {
      background: ${designTokens.colors.primary[600]};
      border-color: ${designTokens.colors.primary[600]};
    }
  `}

  @media (max-width: 900px) {
    min-width: 28px;
    padding: 5px 8px;
    font-size: 0.75rem;
  }
`;

// Checkbox for table rows
export const TableCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  cursor: pointer;
  accent-color: ${designTokens.colors.primary[500]};
`;

export default TableContainer;
